import React, { useEffect, useMemo, useState } from "react";
import { Row, Col } from "reactstrap";
import CustomForm from "../../custom/Forms/CustomForm.js";
import { useNavigate, useParams } from "react-router-dom";
import { createPageData, basicFormData } from "./TechnicianStockReturnFile.js";
import CheckboxInput from "../../custom/components/CheckBox-Input/CheckboxInput.js";
import { validate } from "../../custom/validations/Validate.js";
import { getRecords, saveRecords } from "../../services/api.service.js";
import { toast } from "react-toastify";
import { LoaderCreatePage } from "../Loader/Loader.js";
import { loaderOverlay } from "../../Style/loaderOverly.js";
import NumberInput from "../../custom/components/Number-Input/NumberInput.js";
import SelectInput from "../../custom/components/Select-Input/SelectInput.js";
import TextInput from "../../custom/components/Text-Input/TextInput.js";

const TechnicianStockReturnCreate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    payload,
    navigatePage,
    headerName,
    validationType,
    endpoint,
    getEndpoint,
  } = createPageData();

  const schema = localStorage.getItem("schema");
  const initialPayload = useMemo(() => payload, []);
  const [loadingData, setLoadingData] = useState(id ? true : false);
  const [formData, setFormData] = useState(basicFormData());
  const [formErrors, setFormErrors] = useState({});
  // role
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [technicianSelectOptions, setTechnicianSelectOptions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemSelectOptions, setItemSelectOptions] = useState([]);
  // read-only, auto-fetched from the selected GMDS Code's material master
  const [itemDescription, setItemDescription] = useState("");

  useEffect(() => {
    if (id) {
      getEditData(id);
    }
  }, [id]);

  useEffect(() => {
    getTechniciansData();
    geMaterialsData();
  }, []);

  const getEditData = async (id) => {
    setLoadingData(true);
    try {
      let payload = {
        modelName: initialPayload?.modelName,
        schema: initialPayload?.schema,
        include: initialPayload?.include,
      };

      if (id) {
        payload.whereCondition = { id: id };
      }
      const responseData = await getRecords(payload, getEndpoint);

      if (responseData?.data && responseData.data.length > 0) {
        const data = responseData.data[0];
        setFormData({ inputData: data });
        setSelectedTechnician({
          value: data?.technician_id,
          label: `${data?.technician?.first_name} ${data?.technician?.last_name}`,
        });
        setSelectedItem({
          value: data?.material_id,
          label: data?.material?.gmds_code || data?.material?.name || "-",
        });
        setItemDescription(data?.material?.descriptions || "");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoadingData(false);
    }
  };

  const getTechniciansData = async () => {
    setLoadingData(true);
    try {
      let payload = {
        modelName: "user_masters",
        schema: schema,
        whereCondition: {
          is_active: true,
          role_id: 2,
        },
      };

      const responseData = await getRecords(payload, "/getRecords");
      const newArray = responseData.data?.map((item) => ({
        value: item.id,
        label: `${item.first_name} ${item.last_name}`,
      }));
      setTechnicianSelectOptions(newArray);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setLoadingData(false);
  };

  const geMaterialsData = async () => {
    setLoadingData(true);
    try {
      let payload = {
        modelName: "material_masters",
        schema: schema,
        whereCondition: {
          is_active: true,
        },
        include: [
          {
            model: "category_masters",
            as: "category",
            where: {
              type: 1,
            },
            required: true,
          },
        ],
      };

      const responseData = await getRecords(payload, "/getRecords");
      const newArray = responseData.data?.map((item) => ({
        value: item.id,
        label: item.gmds_code || item.name,
        // carried on the option so selecting a GMDS Code can auto-fill Item description
        descriptions: item.descriptions,
      }));
      setItemSelectOptions(newArray);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setLoadingData(false);
  };

  const onInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prevState) => {
      const updatedValue =
        type === "checkbox"
          ? checked
            ? 1
            : 0
          : name === "alt_mobile_number" && value === ""
            ? null
            : value;

      const updatedInputData = {
        ...prevState.inputData,
        [name]: updatedValue,
      };

      // 🔹 Custom validation for return_qty
      setFormErrors((prevFormErrors) => {
        let errors = { ...prevFormErrors };

        const fieldError = validate(validationType, { [name]: value });
        errors[name] = fieldError[name] || null;

        if (
          name === "return_qty" &&
          Number(updatedInputData.return_qty) >
            Number(updatedInputData.available_qty)
        ) {
          errors.return_qty =
            "Return quantity cannot be greater than Available Quantity";
        } else if (name === "return_qty") {
          errors.return_qty = null;
        }

        return errors;
      });

      return {
        ...prevState,
        inputData: updatedInputData,
      };
    });
  };

  const handleTechnicianSelect = async (options) => {
    const selectedValue = options ? options.value : "";
    const fieldError = validate(validationType, {
      technician_id: selectedValue,
    });

    setSelectedTechnician(options);
    setFormErrors((prevformErrors) => ({
      ...prevformErrors,
      technician_id: fieldError?.technician_id || "",
    }));

    setFormData((prevState) => ({
      ...prevState,
      inputData: {
        ...prevState.inputData,
        technician_id: selectedValue,
      },
    }));
  };
  const handleItemSelect = async (options) => {
    const selectedValue = options ? options.value : "";
    const fieldError = validate(validationType, {
      material_id: selectedValue,
    });

    setSelectedItem(options);
    setItemDescription(options ? options.descriptions || "" : "");
    setFormErrors((prevformErrors) => ({
      ...prevformErrors,
      material_id: fieldError?.material_id || "",
    }));

    setFormData((prevState) => ({
      ...prevState,
      inputData: {
        ...prevState.inputData,
        material_id: selectedValue,
      },
    }));
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      setLoadingData(true);

      const validationformErrors = validate(validationType, formData.inputData);
      setFormErrors(validationformErrors);
      if (JSON.stringify(validationformErrors) !== "{}") return;

      if (
        formData?.inputData?.available_qty < formData?.inputData?.return_qty
      ) {
        setFormErrors((prevFormErrors) => {
          let errors = { ...prevFormErrors };
          errors.return_qty =
            "Return quantity cannot be greater than Available Quantity";
          return errors;
        });
        return;
      }

      let payload = {
        modelName: initialPayload?.modelName,
        schema: initialPayload?.schema,
        inputData: {
          ...formData?.inputData,
          return_qty: parseInt(formData?.inputData?.return_qty),
          available_qty: 20,
        },
      };

      if (id) {
        // update
        // formData.inputData.updated_by = id ? userDetails?.id || "" : "";
        payload = { ...payload, id: id, inputData: formData.inputData };
      } else {
        payload = { ...payload, inputData: formData.inputData };
      }

      // console.log(payload);
      // return;

      const responseData = await saveRecords(payload, endpoint);

      // Handle response
      if (responseData && responseData.code === 1) {
        toast.success(
          `${headerName} ${id ? "updated" : "saved"} successfully!`,
        );
        navigate(navigatePage?.back);
      } else {
        toast.error(
          responseData.message || `Error while saving ${headerName}!`,
        );
      }
    } catch (error) {
      console.error("Error during submission:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setLoadingData(false);
    }
  };

  const getAvailableQty = async (technicianId, materialId) => {
    setLoadingData(true);
    try {
      const availableQtyPayload = {
        technician_id: technicianId,
        material_id: materialId,
      };
      const response = await getRecords(
        availableQtyPayload,
        "/materials/available-qty",
      );
      if (response.code) {
        setFormData((prev) => {
          return {
            ...prev,
            inputData: {
              ...prev.inputData,
              available_qty: response?.data?.available_qty,
            },
          };
        });
      }
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong! Please try again");
    }
    setLoadingData(false);
  };

  useEffect(() => {
    if (selectedItem?.value && selectedTechnician?.value) {
      getAvailableQty(selectedTechnician?.value, selectedItem?.value);
    }
  }, [selectedItem, selectedTechnician]);

  // add document

  return (
    <CustomForm
      title={id ? "UPDATE " + headerName : "CREATE " + headerName}
      loadingData={loadingData}
      onFormSubmit={handleSubmit}
      handleBackClick={() => navigate(navigatePage?.back)}
      id={id}
    >
      <Row className="mt-2">
        <Col md="6" className="mt-3">
          <SelectInput
            label="Select Engineer"
            options={technicianSelectOptions}
            value={selectedTechnician}
            onSelect={handleTechnicianSelect}
            placeholder="Select Engineer"
            inputClassName="form-control"
            labelClassName="form-label"
            asterisk={true}
            customError={formErrors?.technician_id}
          />
        </Col>
        <Col md="6" className="mt-3">
          <SelectInput
            label="GMDS Code"
            options={itemSelectOptions}
            value={selectedItem}
            onSelect={handleItemSelect}
            placeholder="Select GMDS Code"
            inputClassName="form-control"
            labelClassName="form-label"
            asterisk={true}
            customError={formErrors?.material_id}
          />
        </Col>

        <Col md="6" className="mt-3">
          <TextInput
            label="Item Description"
            name="item_description"
            value={itemDescription}
            onChange={() => {}}
            placeholder="-"
            inputClassName="form-control"
            labelClassName="form-label"
            readOnly={true}
          />
        </Col>

        <Col md="6" className="mt-3">
          <TextInput
            label="Available Quantity"
            name="available_quantity"
            value={formData?.inputData?.available_qty}
            onChange={onInputChange}
            placeholder="Available Quantity"
            disabled={true}
          />
        </Col>
        <Col md="6" className="mt-3">
          <NumberInput
            label="Return Quantity"
            name="return_qty"
            value={formData?.inputData?.return_qty}
            onChange={onInputChange}
            placeholder="Enter Return Quantity"
            inputClassName="form-control"
            labelClassName="form-label"
            asterisk={true}
            customError={formErrors?.return_qty}
          />
        </Col>

        <Col md="6" className="mt-3">
          <TextInput
            label="Item Location"
            name="item_location"
            value={formData?.inputData?.item_location || ""}
            onChange={onInputChange}
            placeholder="Enter Item Location"
            inputClassName="form-control"
            labelClassName="form-label"
            customError={formErrors?.item_location}
          />
        </Col>
      </Row>

      <Row className="mt-4">
        <Col lg="12">
          <CheckboxInput
            name="is_active"
            label="Is Active"
            value={formData?.inputData?.is_active}
            checked={formData?.inputData?.is_active}
            handleTextChange={onInputChange}
            parentClassName="form-check"
            checkBoxClassName="form-check-input"
            labelClassName="form-check-label"
          />
        </Col>
      </Row>
      {loadingData && (
        <div style={loaderOverlay}>
          <LoaderCreatePage />
        </div>
      )}
    </CustomForm>
  );
};

export default TechnicianStockReturnCreate;
